import { ComponentProps, ComponentPropsWithoutRef } from 'react'
import { Button, ButtonVariant } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { useToastContext } from './context'
import { ToastActionConfig } from '../../../types'

interface ToastActionProps extends ComponentPropsWithoutRef<typeof Button> {
  className?: string
}

export function ToastAction({ variant = 'secondary', className, children, ...props }: ToastActionProps) {
  return (
    <Button variant={variant} className={twMerge('mt-2 w-full', className)} {...props}>
      {children}
    </Button>
  )
}

interface ToastActionsProps extends ComponentProps<'div'> {
  actions?: Array<ToastActionConfig>
  keepToastOpen?: boolean
}

export function ToastActions({ actions, keepToastOpen = false, children, className, ...props }: ToastActionsProps) {
  const toastContext = useToastContext()

  function dismissToast() {
    if (toastContext && !keepToastOpen) {
      toastContext.close()
    }
  }

  if (!actions) {
    return (
      <div className={twMerge('flex items-center gap-2.5', className)} {...props}>
        {children}
      </div>
    )
  }

  return (
    <div className={twMerge('grid gap-2.5', actions.length > 1 ? 'grid-cols-2' : 'grid-cols-1', className)} {...props}>
      {actions.map((action, index) => {
        if (action.render) {
          return (
            <div key={index} onClick={dismissToast}>
              {action.render}
            </div>
          )
        }
        return (
          <ToastAction
            key={index}
            variant={action.variant as ButtonVariant}
            tone={action.tone}
            onClick={(e) => {
              action.onClick?.(e)
              dismissToast()
            }}
          >
            {action.children}
          </ToastAction>
        )
      })}
    </div>
  )
}
